import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { navigation } from '../data/navigation.js';
import { church } from '../data/church.js';
import { useAuth } from '../context/AuthContext.jsx';
import './Header.css';

function Chevron() {
  return (
    <svg
      className="nav-link__chevron"
      width="12"
      height="8"
      viewBox="0 0 12 8"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M1 1.5L6 6.5L11 1.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------------ */
/* Desktop dropdown                                                          */
/* ------------------------------------------------------------------------ */

function NavItem({ item, openKey, setOpenKey }) {
  const isOpen = openKey === item.label;
  const itemRef = useRef(null);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  const sectionActive = pathname.startsWith(item.href);

  // Hover opens on desktop, but with a short close delay so the pointer can
  // travel the gap between the trigger and the panel without it vanishing.
  const openNow = () => {
    window.clearTimeout(closeTimer.current);
    setOpenKey(item.label);
  };

  const closeSoon = () => {
    closeTimer.current = window.setTimeout(() => {
      setOpenKey((current) => (current === item.label ? null : current));
    }, 160);
  };

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  if (!item.children) {
    return (
      <li className="header__item">
        <NavLink
          to={item.href}
          className="nav-link"
          data-active={sectionActive ? 'true' : undefined}
        >
          {item.label}
        </NavLink>
      </li>
    );
  }

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        if (isOpen) {
          event.stopPropagation();
          setOpenKey(null);
          itemRef.current?.querySelector('button')?.focus();
        }
        break;
      case 'ArrowDown': {
        event.preventDefault();
        setOpenKey(item.label);
        // Defer so the panel is visible (and focusable) before we move focus.
        window.requestAnimationFrame(() => {
          itemRef.current?.querySelector('.dropdown__link')?.focus();
        });
        break;
      }
      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault();
          const links = itemRef.current?.querySelectorAll('.dropdown__link');
          links?.[links.length - 1]?.focus();
        }
        break;
      default:
        break;
    }
  };

  // Roving arrow-key movement between items inside the open panel.
  const handlePanelKeyDown = (event) => {
    const links = Array.from(itemRef.current?.querySelectorAll('.dropdown__link') || []);
    const index = links.indexOf(document.activeElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      links[(index + 1) % links.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      links[(index - 1 + links.length) % links.length]?.focus();
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      setOpenKey(null);
      itemRef.current?.querySelector('button')?.focus();
    } else if (event.key === 'Tab' && !event.shiftKey && index === links.length - 1) {
      setOpenKey(null);
    }
  };

  return (
    <li
      className="header__item"
      ref={itemRef}
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className="nav-link"
        aria-expanded={isOpen}
        aria-haspopup="true"
        data-active={sectionActive ? 'true' : undefined}
        onClick={() => setOpenKey(isOpen ? null : item.label)}
      >
        {item.label}
        <Chevron />
      </button>

      <ul
        className={`dropdown${isOpen ? ' dropdown--open' : ''}`}
        onKeyDown={handlePanelKeyDown}
      >
        {item.children.map((child) => (
          <li key={child.href}>
            <NavLink
              to={child.href}
              className="dropdown__link"
              tabIndex={isOpen ? 0 : -1}
              onClick={() => setOpenKey(null)}
              data-active={pathname === child.href ? 'true' : undefined}
            >
              {child.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </li>
  );
}

/* ------------------------------------------------------------------------ */
/* Mobile drawer                                                             */
/* ------------------------------------------------------------------------ */

function Drawer({ onClose }) {
  const [expanded, setExpanded] = useState(null);
  const { pathname } = useLocation();
  const panelRef = useRef(null);

  // Lock body scroll and move focus into the drawer while it's open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector('button')?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="drawer" role="dialog" aria-modal="true" aria-label="Site menu" ref={panelRef}>
      <div className="drawer__head">
        <Link to="/" onClick={onClose}>
          <img src="/assets/met_logo_md.png" alt={church.name} />
        </Link>
        <button type="button" className="drawer__close" onClick={onClose} aria-label="Close menu">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path
              d="M1 1L17 17M17 1L1 17"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <nav className="drawer__body" aria-label="Main">
        {navigation.map((item) => {
          const isOpen = expanded === item.label;

          if (!item.children) {
            return (
              <div className="drawer__section" key={item.label}>
                <NavLink to={item.href} className="drawer__trigger" onClick={onClose}>
                  {item.label}
                </NavLink>
              </div>
            );
          }

          return (
            <div className="drawer__section" key={item.label}>
              <button
                type="button"
                className="drawer__trigger"
                aria-expanded={isOpen}
                onClick={() => setExpanded(isOpen ? null : item.label)}
              >
                {item.label}
                <Chevron />
              </button>

              {isOpen && (
                <div className="drawer__panel">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.href}
                      to={child.href}
                      className="drawer__link"
                      onClick={onClose}
                      data-active={pathname === child.href ? 'true' : undefined}
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="drawer__footer">
          <Link to="/give" className="btn btn--gold btn--block" onClick={onClose}>
            Give
          </Link>
          <Link to="/login" className="btn btn--secondary btn--block" onClick={onClose}>
            Sign In
          </Link>
        </div>

        <address className="drawer__meta" style={{ fontStyle: 'normal' }}>
          {church.address.street}
          <br />
          {church.address.city}, {church.address.state} {church.address.zip}
          <br />
          <a href={church.phoneHref}>{church.phone}</a>
        </address>
      </nav>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Header                                                                    */
/* ------------------------------------------------------------------------ */

export default function Header() {
  const [openKey, setOpenKey] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Any navigation closes whatever menu was open.
  useEffect(() => {
    setOpenKey(null);
    setDrawerOpen(false);
  }, [pathname]);

  // Click outside closes the desktop dropdown.
  useEffect(() => {
    if (!openKey) return undefined;
    const onClick = (e) => {
      if (!e.target.closest('.header__item')) setOpenKey(null);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [openKey]);

  return (
    <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
      <div className="container">
        <div className="header__banner">
          <Link to="/" className="header__logo" aria-label={`${church.name} — home`}>
            <img
              src="/assets/met_logo_lrg.png"
              alt={church.name}
              width="420"
              height="120"
            />
          </Link>

          <div className="header__worship">
            <p className="header__worship-title">
              Join <em>us for</em> Worship
            </p>
            <p className="header__times">
              {church.services.map((s) => (
                <span key={s.label}>
                  {s.label} {s.time}
                </span>
              ))}
            </p>
          </div>
        </div>
      </div>

      <div className="header__bar">
        <div className="container header__bar-inner">
          <nav className="header__nav" aria-label="Main">
            <ul className="header__list">
              {navigation.map((item) => (
                <NavItem
                  key={item.label}
                  item={item}
                  openKey={openKey}
                  setOpenKey={setOpenKey}
                />
              ))}
            </ul>
          </nav>

          <div className="header__actions">
            {isAdmin && (
              <Link to="/admin" className="btn btn--secondary btn--sm">
                Dashboard
              </Link>
            )}
            {!isAuthenticated && (
              <Link to="/login" className="btn btn--secondary btn--sm">
                Sign In
              </Link>
            )}
            <Link to="/give" className="btn btn--gold btn--sm">
              Give
            </Link>
          </div>

          <button
            type="button"
            className="header__toggle"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
              <path
                d="M0 1h18M0 7h18M0 13h18"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
            Menu
          </button>
        </div>
      </div>

      {drawerOpen && <Drawer onClose={() => setDrawerOpen(false)} />}
    </header>
  );
}
